'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import type { FeatureFlags } from 'api/getFeatureFlags';
import { type AnalyticsDataArticleProps } from 'context/AppContext';
import type { GtmContentFields } from 'types/analytics';
import type { StructureTypes } from 'types/cms';
import { getGTMContentFields, trackPageView, trackSearchPageView } from 'utils/analytics';

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}
export const GTMID = 'GTM-5MXC2MF';

type GoogleTagManagerProps = {
  ivwCode: string;
  homeSectionPath?: string;
  articleFields?: AnalyticsDataArticleProps;
  pageType: StructureTypes;
  pageTitle: string;
  adZone: string;
  keywords: Array<string>;
  featureFlags: FeatureFlags;
};

export const GoogleTagManager = ({
  ivwCode,
  homeSectionPath,
  articleFields,
  pageType,
  pageTitle,
  featureFlags,
  adZone,
  keywords,
}: GoogleTagManagerProps) => {
  const pathname = usePathname();
  const searchTerm = useSearchParams().get('q') ?? '';

  const fields: GtmContentFields = getGTMContentFields(pageTitle, pageType, homeSectionPath, articleFields);

  useEffect(() => {
    trackPageView(fields, ivwCode, adZone, keywords, featureFlags);

    if (pathname === '/suche/') {
      trackSearchPageView(searchTerm);
    }

    const eventKeys: { [x: string]: string } = {
      video_redirect_when_paused: 'video_paused',
      video_redirect_when_finished: 'video_ended',
    };

    window.addEventListener('message', function (e) {
      if (
        e.origin === 'https://emb.player.streamingtech.de' &&
        e.data &&
        ['video_redirect_when_paused', 'video_redirect_when_finished'].includes(e.data.action)
      ) {
        window.dataLayer.push({
          event: 'rtlplus_link_clicked_embed_player',
          video_status: eventKeys[e.data.action],
        });
      }
    });
  }, [pathname, fields, adZone, ivwCode, keywords, searchTerm, featureFlags, pageType]);

  return (
    <>
      <Script
        id="gtm-script-content"
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTMID}');`,
        }}
      />
      <noscript
        dangerouslySetInnerHTML={{
          __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTMID}" height="0" width="0" style="display:none;visibility:hidden" title="Google Tag Manager"></iframe>`,
        }}
      />
    </>
  );
};
